/**
 * The GitHub connection as Settings › Context shows it — the provider card with its status badge,
 * message and install/connect actions, and the "access requested" reminder above it. One component, so
 * the setup panel's repo step and the "Link repos" sheet tell the same story as Settings (ACT2-1108).
 * The state comes from `summarizeGithubConnection`.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { ActionTooltip } from '@/_shared/components/ActionTooltip';
import { Badge } from '@/_shared/components/ui/badge';
import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { cn } from '@/_shared/lib/utils';

import { type ListReposResult, summarizeGithubConnection } from '../lib/githubConnection';
import { GithubReposSheet } from './GithubReposSheet';
import { ScmProviderIcon } from './ScmProviderIcon';

const CONNECT_ADMIN_ONLY = 'Only workspace admins can connect the GitHub App';

/**
 * `card` is Settings' bordered card. `flat` drops the border, padding and icon tile and stacks the actions
 * under the text, for a place that is already a card (the setup panel's repo step), so
 * it is not a card inside a card. Same states and actions either way.
 *
 * `card` wraps instead of a rigid `sm:flex-row`: long action labels ("Connect existing installation")
 * beside the description in a narrow column used to crush the copy to one word per line.
 */
export type GithubConnectionVariant = 'card' | 'flat';

const CONTAINER_CLASSES: Record<GithubConnectionVariant, string> = {
  card: 'flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border px-4 py-4 sm:gap-4',
  flat: 'flex flex-col gap-2',
};

const ICON_CLASSES: Record<GithubConnectionVariant, string> = {
  card: 'size-10 rounded-lg bg-muted',
  flat: 'size-4',
};

interface GithubConnectionProps {
  /** `settings.scm.listRepos`. */
  data: ListReposResult;
  variant?: GithubConnectionVariant;
}

/** The connection card and, while an install request waits, its reminder. */
export function GithubConnectionCard({ data, variant = 'card' }: GithubConnectionProps): React.ReactElement {
  const flat = variant === 'flat';
  const buttonSize = flat ? 'sm' : 'md';
  const { isAdmin, workspace } = useWorkspace();
  const orpc = useOrpc();
  const queryClient = useQueryClient();

  // Which button started the GitHub sign-in, so only that one shows the spinner.
  const [startedFrom, setStartedFrom] = useState<'banner' | 'existing' | null>(null);
  // Set once the sign-in URL is in hand: the mutation settles before the browser leaves the page, so
  // `isPending` alone would re-enable both buttons for that gap.
  const [navigating, setNavigating] = useState(false);
  useEffect(() => {
    // A Back navigation can restore this page from the back-forward cache with `navigating` still set.
    const onPageShow = (event: PageTransitionEvent): void => {
      if (event.persisted) setNavigating(false);
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);
  const startConnect = useMutation(
    orpc.settings.scm.startInstallationConnect.mutationOptions({
      onSuccess: ({ url }) => {
        setNavigating(true);
        window.location.assign(url);
      },
      onError: (err) => toast.error(err.message || 'Could not start the GitHub sign-in. Please try again.'),
    }),
  );
  const connectBusy = startConnect.isPending || navigating;
  const start = (from: 'banner' | 'existing'): void => {
    setStartedFrom(from);
    startConnect.mutate(undefined);
  };

  const dismiss = useMutation(
    orpc.settings.scm.dismissInstallRequest.mutationOptions({
      onSuccess: () => void queryClient.invalidateQueries({ queryKey: orpc.settings.scm.listRepos.key() }),
      onError: (err) => toast.error(err.message || 'Failed to dismiss the reminder'),
    }),
  );

  // The defaults cover a server that predates these fields (a rolling deploy).
  const { repos, githubStatus, installations = [], pendingInstallRequest = null } = data;
  const hasRepos = repos.length > 0;
  const summary = summarizeGithubConnection(data);
  const appActionLabel = installations.length > 0 ? 'Configure app' : 'Install app';
  // Lookout cannot see which GitHub org is this workspace's until the admin signs in, so this offers to look,
  // not a claim that an installation exists. Once one org is linked, its only use is adding another.
  const connectExistingLabel =
    installations.length > 0 ? 'Connect another GitHub org' : 'Connect existing installation';

  return (
    <>
      {githubStatus === 'app' && pendingInstallRequest && (
        <Callout variant="warning" className="mb-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p>
              GitHub access requested {dayjs(pendingInstallRequest.requestedAt).format('MMM D')}. Once a GitHub org
              owner approves the Lookout app in GitHub, connect it here.
            </p>
            <div className="flex shrink-0 flex-wrap items-center gap-2 p-0.5">
              <ActionTooltip show={!isAdmin} message={CONNECT_ADMIN_ONLY}>
                <Button
                  variant="primary"
                  label="Check approval"
                  disabled={!isAdmin || connectBusy}
                  loading={connectBusy && startedFrom === 'banner'}
                  onClick={() => start('banner')}
                />
              </ActionTooltip>
              <ActionTooltip show={!isAdmin} message={CONNECT_ADMIN_ONLY}>
                <Button
                  variant="tertiary"
                  label="Dismiss"
                  // Mid-flow, Dismiss would delete the reminder the Check approval round trip is about to clear.
                  disabled={!isAdmin || connectBusy}
                  loading={dismiss.isPending}
                  onClick={() => dismiss.mutate(undefined)}
                />
              </ActionTooltip>
            </div>
          </div>
        </Callout>
      )}

      <div className={CONTAINER_CLASSES[variant]}>
        <div className={cn('flex min-w-0 gap-3', flat ? 'items-start gap-2' : 'flex-1 basis-48 items-center')}>
          <div
            className={cn(
              'flex shrink-0 items-center justify-center text-muted-foreground',
              ICON_CLASSES[variant],
              flat && 'mt-0.5',
            )}
          >
            <ScmProviderIcon size={flat ? 16 : 20} />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h4 className="text-sm font-semibold text-foreground">{summary.title}</h4>
              <Badge variant={summary.status.variant}>{summary.status.label}</Badge>
            </div>
            <p className={cn('text-xs text-muted-foreground', flat ? 'mt-0.5' : 'mt-1')}>{summary.description}</p>
          </div>
        </div>
        {(hasRepos || githubStatus === 'app') && (
          <div className={cn('flex max-w-full shrink-0 flex-wrap items-center gap-2', flat && 'p-0.5')}>
            {hasRepos && <GithubReposSheet repos={repos} />}
            {githubStatus === 'app' && (
              <ActionTooltip show={!isAdmin} message="Only workspace admins can configure the GitHub App">
                {isAdmin ? (
                  <Button asChild variant="secondary" size={buttonSize}>
                    <a href={`/setup/github/start?workspaceId=${workspace.id}`}>{appActionLabel}</a>
                  </Button>
                ) : (
                  <Button variant="secondary" size={buttonSize} disabled label={appActionLabel} />
                )}
              </ActionTooltip>
            )}
            {githubStatus === 'app' && (
              <ActionTooltip show={!isAdmin} message={CONNECT_ADMIN_ONLY}>
                <Button
                  variant="secondary"
                  size={buttonSize}
                  label={connectExistingLabel}
                  disabled={!isAdmin || connectBusy}
                  loading={connectBusy && startedFrom === 'existing'}
                  onClick={() => start('existing')}
                />
              </ActionTooltip>
            )}
          </div>
        )}
      </div>
    </>
  );
}

/**
 * ACT2-1108: the GitHub step shown where a repo would be linked (the setup panel's repo step, the "Link
 * repos" sheet) while `canLinkRepos` is false — Settings › Context's card, and a link to it.
 */
export function GithubConnectionStep({ data, variant = 'card' }: GithubConnectionProps): React.ReactElement {
  const { wp } = useWorkspace();
  return (
    <div className={cn('flex flex-col', variant === 'flat' ? 'gap-2' : 'gap-3')}>
      <p className="text-sm text-foreground">Linking repos needs GitHub. First, connect it.</p>
      <GithubConnectionCard data={data} variant={variant} />
      <p className="text-xs text-muted-foreground">
        Manage the connection in{' '}
        <Link to={wp('/settings/context')} className="text-primary hover:underline">
          Settings › Context
        </Link>
        .
      </p>
    </div>
  );
}
